<script setup lang="ts" generic="T">
import type { IHeaderNav, IIndexHeader, IPaginationNav, IPaginationApi } from "~/utils/interfaces/table-interfaces";
import IndexGrid from "~/components/Indexs/IndexGrid.vue";
import TabNavigation from "~/components/Common/Navigations/TabNavigation.vue";
import EmptyState from "~/components/Common/EmptyState.vue";
import type { PropType } from "vue";

const props = defineProps({
  nav: {
    type: Array as PropType<IHeaderNav[]>,
  },
  headers: {
    type: Array as PropType<IIndexHeader[]>,
  },
  rows: {
    type: [Object, Array] as PropType<IPaginationApi<T> | T[]>,
  },
  itemPerPage: { type: Number },
  showKpi: { type: Boolean },
  checkbox: { type: Boolean },
  checkBoxReadOnly: { type: Boolean },
  selectAll: { type: Boolean },
  resetNav: { type: Number },
  tabSelected: { type: String },
  filterKey: {
    type: String,
    default: "filter",
  },
  tabFilterKey: {
    type: String,
    default: "name",
  },
  url: { type: String, required: false, default: "" },
  itemKeys: { type: Array as PropType<string[]>, required: false, default: [] },
  store: { type: Object, required: false },
  trackBy: {
    type: String,
    required: false,
    default: "id",
  },
  empty: {
    type: Boolean,
    required: false,
    default: false,
  },
  scope: {
    type: String,
    required: false,
  },
  resize: {
    type: Boolean,
    required: false,
    default: false,
  },
  isLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
  equalColumns: {
    type: Boolean,
    default: false,
  },
  pageContainer: {
    type: Boolean,
    default: true,
  },
  paginationKey: {
    type: String,
    required: false,
  },
  maxHeight: {
    type: String,
    required: false,
    default: "calc(var(--app-viewport-height) - var(--header_height))",
  },
  noPagination: {
    type: Boolean,
    required: false,
    default: false,
  },
  bordered: {
    type: Boolean,
    required: false,
    default: false,
  },
  paginationDisabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  // Current search: when it matches nothing, #headerButtons stays mounted and #empty is replaced.
  search: {
    type: String as PropType<string | null>,
    required: false,
    default: null,
  },
});

const indexGrid = ref();
const selectedItems = ref<Record<string, any>[]>([]);
const excludedItems = ref<Record<string, any>[]>([]);

const emit = defineEmits(["clickRow", "pageNavigate", "tabNavigate", "sort"]);

defineExpose({ selectedItems, excludedItems });

const rowsForGrid = computed<IPaginationApi<T>>(() => {
  if (Array.isArray(props.rows)) {
    return {
      meta: {
        count: props.rows.length,
        totalCount: null,
      },
      data: props.rows,
    };
  }

  return (
    (props.rows as IPaginationApi<T> | undefined) ?? {
      meta: {
        count: 0,
        totalCount: null,
      },
      data: [],
    }
  );
});

const noPaginationForGrid = computed(() => props.noPagination || Array.isArray(props.rows));

// Search the displayed rows were loaded with — the input clears before the debounced refetch lands.
const loadedSearch = ref<string | null>(null);
watch(
  () => props.rows,
  () => (loadedSearch.value = props.search),
  { immediate: true },
);
const isNoResult = computed(() => !!loadedSearch.value?.trim() && !rowsForGrid.value.data?.length);
// No result: embedded grids show the no-result state, full-page grids keep their bare table.
const isGridEmpty = computed(() => (isNoResult.value ? !props.pageContainer : props.empty));

function clickRow(val: any) {
  emit("clickRow", val);
}

function pageNavigate(val: IPaginationNav) {
  emit("pageNavigate", val);
}

function tabNavigate(val: IHeaderNav) {
  emit("tabNavigate", val);
}

function sort(val: IIndexHeader) {
  emit("sort", val);
}

watch(
  () => selectedItems?.value,
  () => {
    indexGrid.value.selectedItems = selectedItems?.value;
  },
);

watch(
  () => indexGrid.value?.selectedItems,
  () => {
    selectedItems.value = indexGrid.value?.selectedItems;
  },
);

watch(
  () => indexGrid.value?.excludedItems,
  () => {
    excludedItems.value = indexGrid.value?.excludedItems;
  },
);

watch(
  () => excludedItems?.value,
  () => {
    indexGrid.value.excludedItems = excludedItems?.value;
  },
);
</script>

<template>
  <div
    :class="{ page_container: props.pageContainer }"
    class="table_container"
    :style="{ height: props.pageContainer && props.maxHeight ? props.maxHeight : undefined }"
  >
    <div class="index_layout">
      <div class="index_header">
        <div class="index_header_left">
          <div class="filters_container">
            <slot name="filters"></slot>
          </div>
        </div>
        <div v-if="!props.empty || isNoResult" class="index_header_right">
          <slot name="headerButtons"> </slot>
        </div>
      </div>
      <div class="flex flex-col flex-1 min-h-0" :class="{ index_bordered: props.bordered && !isGridEmpty }">
        <TabNavigation
          v-if="props?.nav?.length"
          :headers-nav="nav"
          :show-hide-block="props?.showKpi"
          :reset-nav="props.resetNav"
          :tab-selected="props.tabSelected"
          :filter-key="props.filterKey"
          :is-loading="props.isLoading"
          :tab-filter-key="props.tabFilterKey"
          :pagination-key="props.paginationKey"
          @tab-navigate="tabNavigate"
        />
        <!-- Optional row between the tabs and the grid (e.g. KPI strip). Renders nothing when unused. -->
        <div v-if="$slots.underTabs" class="shrink-0">
          <slot name="underTabs"></slot>
        </div>
        <IndexGrid
          ref="indexGrid"
          :selected-nav="props?.nav?.find((x) => (x as any)[props.filterKey] === props.tabSelected) as IHeaderNav"
          :check-box-read-only="props.checkBoxReadOnly"
          :headers="headers as IIndexHeader[]"
          :rows="rowsForGrid"
          :track-by="trackBy"
          :item-per-page="itemPerPage as number"
          :show-kpi="props?.showKpi"
          :checkbox="props.checkbox"
          :select-all="props.selectAll"
          :url="props.url"
          :item-keys="props.itemKeys"
          :store="props.store"
          :empty="isGridEmpty"
          :no-pagination="noPaginationForGrid"
          :pagination-disabled="props.paginationDisabled"
          :scope="props.scope"
          :resize="props.resize"
          :equal-columns="props.equalColumns"
          :pagination-key="props.paginationKey"
          :page-container="props.pageContainer"
          :nav="!!props.nav"
          @click-row="clickRow"
          @page-navigate="pageNavigate"
          @sort="sort"
        >
          <template #row="row">
            <slot name="row" :row="row.row"></slot>
          </template>
          <!-- Pages write the "no data" state; the shared no-result one is overridable via #emptyFiltered. -->
          <template #empty>
            <slot v-if="isNoResult" name="emptyFiltered">
              <EmptyState message="Aucun résultat ne correspond à votre recherche." icon="search" />
            </slot>
            <slot v-else name="empty"></slot>
          </template>
        </IndexGrid>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "assets/scss/texts";

.page_container {
  @apply flex justify-center pt-[18px] pb-3;
}

.index_layout {
  @apply flex flex-col gap-[16px] w-full flex-1 min-h-0;
}

.index_header {
  @apply flex flex-row justify-between items-start px-4;
}

.index_header_right {
  @apply flex flex-row gap-[8px];
}

.index_header_left {
  @apply flex flex-row gap-[8px] items-start;
}

.filters_container {
  @extend .body_regular;
  @apply flex flex-wrap gap-3 text-secondary-color mr-[8px] z-[150];
}

.index_bordered :deep(.grid__container) {
  /* overflow-auto (not -hidden): still clips the rounded corners, but lets a wide
     table scroll horizontally instead of being cut off. */
  @apply border border-stroke-color rounded-lg overflow-auto;
}
.index_bordered :deep(.grid__container thead th) {
  @apply border-t-0;
}
.index_bordered :deep(.grid__container tbody tr:last-child td) {
  @apply border-b-0;
}
.index_bordered :deep(.grid__container + *) {
  @apply border-t-0;
}
</style>
